import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Toast, type ToastType } from "@legal-500/legal500-design-system";
import { usePermissions } from "@/hooks/usePermissions";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { researchCms, fetchAllEditions } from "@/api/researchCms.service";
import { TaxonomyBpaTierSelector } from "@/components/ResearchCms/TaxonomyBpaTierSelector/TaxonomyBpaTierSelector";
import { CreateRankingEntityModal } from "@/components/ResearchCms/CreateRankingEntityModal/CreateRankingEntityModal";
import { CreatePracticeGroupModal } from "@/components/ResearchCms/CreatePracticeGroupModal/CreatePracticeGroupModal";
import {
  AsyncSearchableSelect,
  type AsyncSelectOption,
} from "@/components/Form/AsyncSearchableSelect/AsyncSearchableSelect";
import { UnsavedChangesModal } from "../../../components/ResearchCms/UnsavedChangesModal/UnsavedChangesModal";
import { UnauthorizedView } from "../../../components/UnauthorizedView";
import { ROUTES } from "../../../routes";
import { CmsLayout } from "./components/CmsLayout";
import {
  BpaAssignmentFields,
  BpaEditionLinkFields,
  RankingEntityPickerField,
  useBpaFormSearches,
} from "./components/BpaFormFields";
import { NewRankingTablesFields } from "./components/BpaFormFields/NewRankingTablesFields";
import { useNavigationGuard } from "./hooks/useNavigationGuard";
import shared from "./components/cmsShared.module.css";
import styles from "./CmsCreateBpaPage.module.css";

const rankingTableSchema = z.object({
  name: z.string().min(1, "Required"),
  subtype: z.string().min(1, "Required"),
  submissionType: z.string(),
  refereeType: z.string(),
});

const createBpaSchema = z.object({
  name: z.string().min(1, "Name is required"),
  rankingEntityId: z.string().min(1, "Ranking entity is required"),
  practiceGroupId: z.string().nullable(),
  taxonomyBpaId: z.string().nullable(),
  sourceEditionBpaId: z.string().nullable(),
  previousEditionBpaId: z.string().nullable(),
  assignedResearcherId: z.string().nullable(),
  assignedEditorId: z.string().nullable(),
  rankingTables: z.array(rankingTableSchema),
});

type CreateBpaFormValues = z.infer<typeof createBpaSchema>;

export function CmsCreateBpaPage() {
  const { hasPermission } = usePermissions();
  const { id: publicationConfigId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const editionConfigId = searchParams.get("edition") ?? "";

  const editionsQuery = useQuery({
    queryKey: ["research-all-editions"],
    queryFn: () => fetchAllEditions(),
  });
  const editions = editionsQuery.data ?? [];
  const editionConfig = editions.find(e => e.id === editionConfigId);
  const editionId = editionConfig?.editionId ?? "";
  const editionName = editionConfig?.editionName ?? "";
  const previousEditionConfigId = editionConfig?.previousEditionId ?? null;
  const previousEditionId = editions.find(e => e.id === previousEditionConfigId)?.editionId ?? null;
  const editionNamesByEditionId = useMemo(
    () => new Map(editions.map(e => [e.editionId, e.editionName ?? e.editionId])),
    [editions],
  );

  const researchersQuery = useQuery({
    queryKey: ["research-researchers"],
    queryFn: () => researchCms.getResearchers(),
  });
  const researchers = useMemo(
    () => (researchersQuery.data?.researchers ?? []).map(r => ({ value: r.id, label: r.name })),
    [researchersQuery.data],
  );

  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const [showNewEntity, setShowNewEntity] = useState(false);
  const [selectedEntityName, setSelectedEntityName] = useState("");

  const [showNewGroup, setShowNewGroup] = useState(false);
  const [createdGroupOption, setCreatedGroupOption] = useState<AsyncSelectOption | null>(null);

  const form = useForm<CreateBpaFormValues>({
    resolver: zodResolver(createBpaSchema),
    defaultValues: {
      name: "",
      rankingEntityId: "",
      practiceGroupId: null,
      taxonomyBpaId: null,
      sourceEditionBpaId: null,
      previousEditionBpaId: null,
      assignedResearcherId: null,
      assignedEditorId: null,
      rankingTables: [],
    },
  });

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    reset,
    formState: { errors, isDirty },
  } = form;

  const navGuard = useNavigationGuard(isDirty);

  const rankingEntityId = watch("rankingEntityId");

  const { searchPracticeGroups, searchResearchers } = useBpaFormSearches({
    rankingEntityId,
    editionId,
    previousEditionId,
    researchers,
    editionNamesByEditionId,
  });

  function handleEntityCreated(entity: { id: string; name: string; type: string }) {
    setValue("rankingEntityId", entity.id, { shouldDirty: true });
    setSelectedEntityName(`${entity.name} (${entity.type})`);
    setShowNewEntity(false);
    queryClient.invalidateQueries({ queryKey: ["research-ranking-entities"] });
  }

  function handleGroupCreated(group: { id: string; name: string }) {
    setValue("practiceGroupId", group.id, { shouldDirty: true });
    setCreatedGroupOption({ value: group.id, label: group.name });
    setShowNewGroup(false);
  }

  async function onSubmit(data: CreateBpaFormValues) {
    if (!editionId) return;
    setIsSaving(true);
    try {
      const result = await researchCms.createResearchBpa({
        ...data,
        editionId,
        rankingTables: data.rankingTables.map(rt => ({
          ...rt,
          submissionType: rt.submissionType || null,
          refereeType: rt.refereeType || null,
        })),
      });
      queryClient.invalidateQueries({ queryKey: ["research-edition-bpas"] });
      queryClient.invalidateQueries({ queryKey: ["research-bpa-stats"] });
      const newBpaId = result.researchBpa.id as string;
      reset();
      setToast({ message: "Practice area created", type: "success" });
      setTimeout(() => {
        navigate(ROUTES.research.cmsEditBpa(publicationConfigId ?? "", newBpaId, editionConfigId));
      }, 800);
    } catch (err) {
      setError("root.serverError", {
        type: "server",
        message: err instanceof Error ? err.message : "Failed to create practice area",
      });
    } finally {
      setIsSaving(false);
    }
  }

  if (!hasPermission("staff.research.create")) return <UnauthorizedView />;

  return (
    <CmsLayout
      breadcrumbs={[
        {
          label: editionName || "Edition",
          path: editionId ? ROUTES.research.cmsPublication(editionId) : "",
        },
        {
          label: "Manage",
          path: ROUTES.research.cmsManage(publicationConfigId ?? "", editionConfigId),
        },
        { label: "Create practice area" },
      ]}
    >
      <div className={styles.page}>
        <h1 className={styles.pageTitle}>Create practice area</h1>
        <p className={styles.pageSubtitle}>
          Add a new practice area to <strong>{editionName || "this edition"}</strong>.
        </p>

        <FormProvider {...form}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className={`${styles.form} ${styles.oneInputLook}`}
          >
            <section className={shared.sectionCard}>
              <h2 className={shared.sectionHeading}>Details</h2>
              <div className={styles.fieldGrid}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="bpa-name">
                    Practice area name{" "}
                    <span aria-hidden="true" className={styles.requiredMark}>
                      *
                    </span>
                  </label>
                  <input
                    id="bpa-name"
                    className={styles.input}
                    {...register("name")}
                    placeholder="e.g. Banking and Finance"
                  />
                  {errors.name && <span className={styles.error}>{errors.name.message}</span>}
                </div>

                <div className={styles.field}>
                  <Controller
                    name="rankingEntityId"
                    control={control}
                    render={({ field }) => (
                      <RankingEntityPickerField
                        value={field.value || null}
                        onChange={val => field.onChange(val ?? "")}
                        onEntityChange={entity =>
                          setSelectedEntityName(entity ? `${entity.name} (${entity.type})` : "")
                        }
                        onCreateNew={() => setShowNewEntity(true)}
                        selectedLabel={selectedEntityName || null}
                        error={errors.rankingEntityId?.message}
                      />
                    )}
                  />
                </div>

                <div className={styles.field}>
                  <Controller
                    name="practiceGroupId"
                    control={control}
                    render={({ field }) => (
                      <AsyncSearchableSelect
                        name="practiceGroupId"
                        label="Practice group"
                        value={field.value}
                        onChange={val => field.onChange(val ?? null)}
                        onSearch={searchPracticeGroups}
                        placeholder={rankingEntityId ? "Search groups..." : "Select entity first"}
                        disabled={!rankingEntityId}
                        minSearchLength={0}
                        initialOption={createdGroupOption}
                      />
                    )}
                  />
                  {rankingEntityId && (
                    <button
                      type="button"
                      className={styles.inlineCreate}
                      onClick={() => setShowNewGroup(true)}
                    >
                      + Create new practice group
                    </button>
                  )}
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>Taxonomy BPA</label>
                  <Controller
                    name="taxonomyBpaId"
                    control={control}
                    render={({ field }) => (
                      <TaxonomyBpaTierSelector
                        value={field.value}
                        onChange={val => field.onChange(val)}
                      />
                    )}
                  />
                </div>
              </div>
            </section>

            <section className={shared.sectionCard}>
              <h2 className={shared.sectionHeading}>Ranking tables</h2>
              <p className={styles.sectionNote}>
                Define the ranking tables for this practice area. Each table has a name and a
                subtype that determines its default behaviour (tiered vs untiered, firm vs lawyer).
              </p>
              <NewRankingTablesFields />
            </section>

            <BpaAssignmentFields control={control} searchResearchers={searchResearchers} />

            <BpaEditionLinkFields
              control={control}
              previousEditionId={previousEditionId}
              editionId={editionId}
              editionNamesByEditionId={editionNamesByEditionId}
            />

            {errors.root?.serverError && (
              <div className={styles.serverError} role="alert">
                {errors.root.serverError.message}
              </div>
            )}

            <div className={shared.footer}>
              <button
                type="button"
                className={shared.discardButton}
                onClick={() =>
                  navigate(ROUTES.research.cmsManage(publicationConfigId ?? "", editionConfigId))
                }
              >
                Cancel
              </button>
              <button type="submit" className={shared.saveButton} disabled={isSaving}>
                {isSaving ? "Creating..." : "Create practice area"}
              </button>
            </div>
          </form>
        </FormProvider>
      </div>

      {navGuard.blocked && (
        <UnsavedChangesModal
          onCancel={navGuard.cancel}
          onDiscard={() => {
            reset();
            navGuard.proceed();
          }}
        />
      )}

      <CreateRankingEntityModal
        open={showNewEntity}
        onClose={() => setShowNewEntity(false)}
        onCreated={handleEntityCreated}
      />

      <CreatePracticeGroupModal
        open={showNewGroup}
        onClose={() => setShowNewGroup(false)}
        rankingEntityId={rankingEntityId}
        rankingEntityName={selectedEntityName}
        onCreated={handleGroupCreated}
      />

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </CmsLayout>
  );
}
